/**
 * File navigation.js.
 *
 * Handles toggling the navigation menu for small screens and enables TAB key
 * navigation support for dropdown menus.
 */
( function() {
	const siteNavigation = document.getElementById( 'site-navigation' );

	// Return early if the navigation doesn't exist.
	if ( ! siteNavigation ) {
		return;
	}

	const button = siteNavigation.getElementsByTagName( 'button' )[ 0 ];

	// Return early if the button doesn't exist.
	if ( 'undefined' === typeof button ) {
		return;
	}

	const menu = siteNavigation.getElementsByTagName( 'ul' )[ 0 ];

	// Hide menu toggle button if menu is empty and return early.
	if ( 'undefined' === typeof menu ) {
		button.style.display = 'none';
		return;
	}

	if ( ! menu.classList.contains( 'nav-menu' ) ) {
		menu.classList.add( 'nav-menu' );
	}

	// Toggle the .toggled class and the aria-expanded value each time the button is clicked.
	button.addEventListener( 'click', function() {
		const isExpanded = button.getAttribute( 'aria-expanded' ) === 'true';
		
		siteNavigation.classList.toggle( 'toggled' );

		if ( isExpanded ) {
			button.setAttribute( 'aria-expanded', 'false' );
		} else {
			button.setAttribute( 'aria-expanded', 'true' );
			// Focus first menu link when opening on mobile
			if ( window.innerWidth <= 1100 ) {
				setTimeout( function() {
					const firstLink = menu.querySelector( 'a' );
					if ( firstLink ) {
						firstLink.focus();
					}
				}, 100 );
			}
		}
	} );

	// Close mobile menu on Escape key
	document.addEventListener( 'keydown', function( event ) {
		if ( event.key === 'Escape' && siteNavigation.classList.contains( 'toggled' ) ) {
			siteNavigation.classList.remove( 'toggled' );
			button.setAttribute( 'aria-expanded', 'false' );
			button.focus();
		}
	} );

	// Close mobile menu when clicking outside (mobile only)
	document.addEventListener( 'click', function( event ) {
		// Only on mobile
		if ( window.innerWidth <= 1100 && siteNavigation.classList.contains( 'toggled' ) ) {
			const isClickInside = siteNavigation.contains( event.target );
			const isButton = button.contains( event.target );
			
			if ( ! isClickInside && ! isButton ) {
				siteNavigation.classList.remove( 'toggled' );
				button.setAttribute( 'aria-expanded', 'false' );
			}
		}
	} );

	// Get all the link elements within the menu.
	const links = menu.getElementsByTagName( 'a' );

	// Get all the link elements with children within the menu.
	const linksWithChildren = menu.querySelectorAll( '.menu-item-has-children > a, .page_item_has_children > a' );

	// Toggle focus each time a menu link is focused or blurred.
	for ( const link of links ) {
		link.addEventListener( 'focus', toggleFocus, true );
		link.addEventListener( 'blur', toggleFocus, true );
	}

	// Function to manage tabindex for submenu links
	function updateSubmenuTabindex( submenu, isOpen ) {
		if ( ! submenu ) return;
		
		const submenuLinks = submenu.querySelectorAll( 'a' );
		for ( const link of submenuLinks ) {
			if ( isOpen ) {
				link.removeAttribute( 'tabindex' );
			} else {
				link.setAttribute( 'tabindex', '-1' );
			}
		}
	}

	// Set initial tabindex for all closed submenus on page load (mobile only)
	function initializeSubmenuTabindex() {
		if ( window.innerWidth <= 1100 ) {
			const allSubmenus = menu.querySelectorAll( '.menu-item-has-children > ul, .page_item_has_children > ul' );
			for ( const submenu of allSubmenus ) {
				const parentLi = submenu.parentNode;
				const isOpen = parentLi.classList.contains( 'submenu-open' );
				updateSubmenuTabindex( submenu, isOpen );
			}
		}
	}

	// Initialize on page load
	initializeSubmenuTabindex();

	// Re-initialize on window resize
	window.addEventListener( 'resize', function() {
		initializeSubmenuTabindex();
	} );

	// Mobile accordion functionality with separate toggle button
	const submenuToggles = menu.querySelectorAll( '.submenu-toggle' );
	for ( const toggle of submenuToggles ) {
		toggle.addEventListener( 'click', function( event ) {
			// Only apply accordion behavior on mobile
			if ( window.innerWidth <= 1100 ) {
				event.preventDefault();
				event.stopPropagation();

				const parentLi = this.parentNode;
				const submenu = parentLi.querySelector( 'ul' );

				if ( submenu ) {
					// Toggle the submenu open state
					const isOpen = parentLi.classList.toggle( 'submenu-open' );
					this.setAttribute( 'aria-expanded', isOpen ? 'true' : 'false' );
					
					// Update tabindex for submenu links
					updateSubmenuTabindex( submenu, isOpen );
				}
			}
		} );
	}

	// Keyboard navigation for toggle buttons
	for ( const toggle of submenuToggles ) {
		toggle.addEventListener( 'keydown', function( event ) {
			const parentLi = this.parentNode;
			const submenu = parentLi.querySelector( 'ul' );

			if ( submenu && window.innerWidth <= 1100 ) {
				// Enter or Space to toggle submenu
				if ( event.key === 'Enter' || event.key === ' ' ) {
					event.preventDefault();
					const isOpen = parentLi.classList.toggle( 'submenu-open' );
					this.setAttribute( 'aria-expanded', isOpen ? 'true' : 'false' );
					
					// Update tabindex for submenu links
					updateSubmenuTabindex( submenu, isOpen );

					// Focus first submenu item if opening
					if ( isOpen ) {
						setTimeout( function() {
							const firstSubmenuLink = submenu.querySelector( 'a' );
							if ( firstSubmenuLink ) {
								firstSubmenuLink.focus();
							}
						}, 100 );
					}
				}
			}
		} );
	}

	// Keyboard navigation for parent links (desktop only)
	for ( const link of linksWithChildren ) {
		link.addEventListener( 'keydown', function( event ) {
			const parentLi = this.parentNode;
			const submenu = parentLi.querySelector( 'ul' );

			// Desktop: Arrow Down to enter submenu
			if ( submenu && window.innerWidth > 1100 ) {
				if ( event.key === 'ArrowDown' ) {
					event.preventDefault();
					const firstSubmenuLink = submenu.querySelector( 'a' );
					if ( firstSubmenuLink ) {
						firstSubmenuLink.focus();
					}
				}
			}
		} );
	}

	// Enhanced keyboard navigation for all menu links
	for ( const link of links ) {
		link.addEventListener( 'keydown', function( event ) {
			// Escape key handling for submenus
			if ( event.key === 'Escape' ) {
				const parentLi = this.closest( 'li' );
				const parentUl = this.closest( 'ul' );
				
				// If we're in a submenu, go back to parent menu item
				if ( parentUl && ! parentUl.classList.contains( 'primary-nav-menu' ) && ! parentUl.classList.contains( 'nav-menu' ) ) {
					event.preventDefault();
					const parentMenuItemLi = parentUl.closest( 'li' );
					const parentMenuItemLink = parentMenuItemLi.querySelector( 'a' );
					if ( parentMenuItemLink ) {
						// Close submenu on mobile
						if ( window.innerWidth <= 1100 ) {
							parentMenuItemLi.classList.remove( 'submenu-open' );
							// Update tabindex when closing via Escape
							updateSubmenuTabindex( parentUl, false );
						}
						parentMenuItemLink.focus();
					}
				}
			}
		} );
	}

	/**
	 * Sets or removes .focus class on an element.
	 */
	function toggleFocus() {
		if ( event.type === 'focus' || event.type === 'blur' ) {
			let self = this;
			// Move up through the ancestors of the current link until we hit .nav-menu.
			while ( ! self.classList.contains( 'nav-menu' ) ) {
				// On li elements toggle the class .focus.
				if ( 'li' === self.tagName.toLowerCase() ) {
					self.classList.toggle( 'focus' );
				}
				self = self.parentNode;
			}
		}
	}

	// Search bar expansion functionality
	function initSearchBar() {
		const searchToggle = document.querySelector( '.search-toggle' );
		const searchBar = document.querySelector( '.utility-search-bar' );
		const searchClose = document.querySelector( '.search-close' );
		const searchInput = document.querySelector( '.utility-search-input' );
		const searchSubmit = document.querySelector( '.search-submit' );
		const utilityBar = document.getElementById( 'utility-bar' );

		console.log('Search elements found:', {
			searchToggle: !!searchToggle,
			searchBar: !!searchBar,
			searchClose: !!searchClose,
			searchInput: !!searchInput,
			searchSubmit: !!searchSubmit,
			utilityBar: !!utilityBar
		});

		// Function to manage tabindex for search bar focusable elements
		function updateSearchTabindex( isOpen ) {
			if ( searchInput ) {
				if ( isOpen ) {
					searchInput.removeAttribute( 'tabindex' );
				} else {
					searchInput.setAttribute( 'tabindex', '-1' );
				}
			}
			if ( searchSubmit ) {
				if ( isOpen ) {
					searchSubmit.removeAttribute( 'tabindex' );
				} else {
					searchSubmit.setAttribute( 'tabindex', '-1' );
				}
			}
		}

		// Initialize tabindex on page load (search starts closed)
		updateSearchTabindex( false );

		if ( searchToggle && searchBar ) {
			const searchText = searchToggle.querySelector('.search-text');
			const searchIcon = searchToggle.querySelector('.search-icon');
			
			// Toggle search bar
			searchToggle.addEventListener( 'click', function(e) {
				e.preventDefault();
				console.log('Search toggle clicked');
				
				const isExpanded = utilityBar.classList.contains('search-expanded');
				
				if ( !isExpanded ) {
					// Open search bar
					utilityBar.classList.add( 'search-expanded' );
					searchBar.setAttribute( 'aria-hidden', 'false' );
					searchToggle.setAttribute( 'aria-expanded', 'true' );
					updateSearchTabindex( true );
					
					// Change button to "Close" with X icon
					if ( searchText ) searchText.textContent = 'Close';
					if ( searchIcon ) searchIcon.classList.add( 'close-mode' );
					
					// Focus on search input after animation
					setTimeout( function() {
						if ( searchInput ) {
							searchInput.focus();
						}
					}, 300 );
				} else {
					// Close search bar
					closeSearchBar();
				}
			} );

			// Close search bar function
			function closeSearchBar() {
				console.log('Closing search bar');
				utilityBar.classList.remove( 'search-expanded' );
				searchBar.setAttribute( 'aria-hidden', 'true' );
				searchToggle.setAttribute( 'aria-expanded', 'false' );
				updateSearchTabindex( false );
				
				// Change button back to "Search" with search icon
				if ( searchText ) searchText.textContent = 'Search';
				if ( searchIcon ) searchIcon.classList.remove( 'close-mode' );
			}


			// Close on escape key
			document.addEventListener( 'keydown', function( event ) {
				if ( event.key === 'Escape' && utilityBar.classList.contains( 'search-expanded' ) ) {
					closeSearchBar();
					searchToggle.focus();
				}
			} );

			// Close search when clicking outside
			document.addEventListener( 'click', function( event ) {
				if ( utilityBar.classList.contains( 'search-expanded' ) ) {
					const isClickInside = searchBar.contains( event.target ) || 
					                      searchToggle.contains( event.target );
					
					if ( ! isClickInside ) {
						closeSearchBar();
					}
				}
			} );

		// Handle search form submission
		if ( searchInput ) {
			function performSearch() {
				const searchTerm = searchInput.value.trim();
				if ( searchTerm ) {
					// Redirect to WordPress search results
					window.location.href = '/?s=' + encodeURIComponent( searchTerm );
				}
			}

			// Handle Enter key in search input
			searchInput.addEventListener( 'keypress', function( event ) {
				if ( event.key === 'Enter' ) {
					event.preventDefault();
					performSearch();
				}
			} );

			// Handle search submit button click
			const searchSubmit = document.querySelector( '.search-submit' );
			if ( searchSubmit ) {
				searchSubmit.addEventListener( 'click', function( event ) {
					event.preventDefault();
					performSearch();
				} );
			}
		}
		} else {
			console.log('Search elements not found - toggle:', !!searchToggle, 'bar:', !!searchBar);
		}
	}

	// Initialize search when DOM is ready
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', initSearchBar );
	} else {
		initSearchBar();
	}


}() );
